<template>
	<div v-if="enrollments.length > 0">
		<div class="form-group fg-line">
			<label>Year</label>
			<select class="form-control" v-model="year">
				<option v-for="year in availableYears" v-bind:value="year">
					{{ year }}
				</option>
			</select>
		</div>
		<div class="form-group fg-line">
			<label>Week</label>
			<select class="form-control" v-model="week">
				<option value="1">1</option>
				<option value="2">2</option>
				<option value="3">3</option>
				<option value="4">4</option>
				<option value="5">5</option>
				<option value="6">6</option>
				<option value="7">7</option>
				<option value="8">8</option>
				<option value="9">9</option>
				<option value="10">10</option>
				<option value="11">11</option>
				<option value="12">12</option>
			</select>
            <div class="simple-error" v-if="form_error">
                {{ form_error }}
            </div>
		</div>
		<div class="form-group fg-line">
			<label>Enrollment</label>
			<select class="form-control" v-model="enrollmentId">
				<option v-for="enrollment in enrollmentsInCurrentYear" v-bind:value="enrollment.id">
					{{ getEnrollmentLabel(enrollment) }}
				</option>
			</select>
		</div>
		<div class="row">
			<div class="form-group fg-line col-sm-6">
				<label style="text-transform: capitalize">{{ getFieldStageLabel(1) }}</label>
				<select class="form-control" v-model="firstCropStage">
					<option v-for="stage in getStagesInCurrentField(1)" v-bind:value="stage.stage">
						{{ stage.stagename }}
					</option>
				</select>
			</div>
			<div class="form-group fg-line col-sm-6">
				<label style="text-transform: capitalize">{{ getFieldStageLabel(2) }}</label>
				<select class="form-control" v-model="secondCropStage">
					<option v-for="stage in getStagesInCurrentField(2)" v-bind:value="stage.stage">
						{{ stage.stagename }}
					</option>
				</select>
			</div>
		</div>
		<button class="btn btn-primary btn-bold" @click="downloadSubform" :href="downloadHREF" :disabled="buttonDisabled">
			Download Subform
		</button>
	</div>
    <div v-else>
        No Enrollments found for this account
    </div>
</template>

<style scoped>

</style>

<script>
	const sortFieldsByPosition = (a, b) => {
		if (a.id < b.id) {
			return -1;
		} else if (a.id > b.id) {
			return 1;
		}
		else {
			return 0;
		}
	}

	/**
	 * Props:
	 * 'account_nbr' : account number (int)
	 * 'enrollments': array containing objects like {
	 *     'id' : 1
	 *     'year' : 2020
	 *     'fields' : [[
	 *         'id' : 60,
	 *         'name' : 'Field 1'
	 *         'crop_name' : 'corn',
	 *         'stages' : [{
	 *             'stage' : 1,
	 *             'stagename' : 'V1'
	 *         }],
	 * 		   'weekly_submissions': [
	* 			    1,
	* 			    2
	 * 			]
	 *     ]]
	 * }
	 */
	export default {
		name: "sharedinsights-download-form",
		props: [
			'account_nbr',
			'enrollments'
		],
		data: function () {
			return {
				week: 1,
				year: this.getFirstYear(this.enrollments),
				enrollmentId: this.getFirstEnrollmentId(this.enrollments),
				firstCropStage:  this.getFirstCropStage(this.enrollments, 1),
				secondCropStage: this.getFirstCropStage(this.enrollments, 2),
				form_error: null
			};
		},
		watch: {
			// When year changes, I want to to change the field to the first field found for that year
			year: function (newYear, oldYear) {
				// Update Field
				this.enrollmentId = this.enrollments.filter(enrollment => enrollment.year === newYear)
					.map(enrollment => enrollment.id)
					.find(() => true);  // First or null
			},
			// When enrollment ID changes, I want to reset the crop stages
			enrollmentId: function (newEnrollmentId, oldEnrollmentId) {
				let enrollment = this.enrollments.find(enrollment => enrollment.id == newEnrollmentId);

				if (enrollment === null) {
					return;
				}
				
				let firstCropField = this.getFieldByPosition(enrollment, 1);

				if (firstCropField) {
					this.firstCropStage = firstCropField.stages
						.map(stage => stage.stage)
						.sort()
						.find(() => true);
				}

				let secondCropField = this.getFieldByPosition(enrollment, 2);

				if (secondCropField) {
					this.secondCropStage = secondCropField.stages
						.map(stage => stage.stage)
						.sort()
						.find(() => true);
				}
			},
			week: function (newWeek, oldWeek) {
				this.form_error = null;
				this.week = newWeek;
			}
		},
		computed: {
			// Lists all years ['2020', '2021']
			availableYears: function () {
				return this.enrollments.map(enrollment => enrollment.year)
					.filter((value, index, self) => self.indexOf(value) === index)    // unique
					.sort();
			},
			enrollmentsInCurrentYear: function () {
				return this.enrollments.filter(enrollment => enrollment.year === this.year);
			},
			currentEnrollment: function () {
				return this.enrollments.find(enrollment => enrollment.id === this.enrollmentId);
			},
			buttonDisabled: function () {
				return this.availableYears.find(year => year === this.year) === null ||
					this.enrollmentsInCurrentYear.find(enrollment => enrollment.id === this.enrollmentId) === null ||
					this.getStagesInCurrentField(1).find(stage => stage.stage === this.firstCropStage) === null ||
					this.getStagesInCurrentField(2).find(stage => stage.stage === this.secondCropStage) === null;
			}
		},
		methods: {
			// <editor-fold desc="Get First Data Values">
			// This method exists because computed fields aren't available during data creation
			getFirstYear: function(enrollments) {
				return enrollments.map(enrollment => enrollment.year)
					.sort()
					.find(() => true);
			},
			getFirstEnrollmentId: function (enrollments) {
				let year = this.getFirstYear(enrollments);

				if (year == null) {
					return null;
				}

				// Find first enrollment of this year's id
				return enrollments.filter(enrollment => enrollment.year === year)
					.map(enrollment => enrollment.id)
					.find(() => true);
			},
			getFieldByPosition: function (enrollment, field_position) {
				let field = null;
				if (field_position == 1) {
					field = enrollment.fields.reduce(function(a, b) {
						return a.id < b.id ? a : b;
					});
				}
				else if (field_position == 2) {
					field = enrollment.fields.reduce(function(a, b) {
						return a.id > b.id ? a : b;
					});
				}
				return field;
			},
			getFirstCropStage: function (enrollments, field_position) {
				let year = this.getFirstYear(enrollments);

				if (year == null) {
					return null;
				}

				// Find first enrollment of this year's id
				let firstEnrollment = enrollments.filter(enrollment => enrollment.year === year)
					.find(() => true);

				if (firstEnrollment == null) {
					return null;
				}

				let field = this.getFieldByPosition(firstEnrollment, field_position);

				if (field == null) {
					return null;
				}

				return field.stages
					.map(stage => stage.stage)
					.find(() => true);
			},
			// </editor-fold>

			// <editor-fold desc="Get Field By Yield Group">
			getFieldStageLabel: function (field_position) {
				let field = this.getFieldByPosition(this.currentEnrollment, field_position);
				return field ? "Crop Stage - " + field.name + " - " + field.crop_name
					: yield_group + " Field Crop Stage";
			},
			getStagesInCurrentField: function (field_position) {
				let field = this.getFieldByPosition(this.currentEnrollment, field_position)
				return field ? field.stages.concat().sort((stage1, stage2) => {
					return stage1.stage - stage2.stage;
				}) : [];
			},
			// </editor-fold>

			getEnrollmentLabel: function (enrollment) {
				return enrollment.fields
					.concat()    // shallow copy because sort does it in place (and just to think, I started to like Javascript)
					.sort(sortFieldsByPosition)
					.map(field => field.name)
					.join(" - ");
			},

			downloadSubform: function() {
				if (this.getFieldByPosition(this.currentEnrollment, 1).weekly_submissions.includes(parseInt(this.week)) &&
					this.getFieldByPosition(this.currentEnrollment, 2).weekly_submissions.includes(parseInt(this.week))) {
					// Set error that because both samples have been received for the week provided
					this.form_error = "* We have already received your samples for this week. Please choose another."
				}
				else {
					// This will open into a separate window because of the '_blank' target
					mwl.submitHiddenForm('/sharedinsights/createSharedInsightsSubForm', {
						'account' : this.account_nbr,
						'week': this.week,
						'year': this.year,
						'enrollmentId': this.enrollmentId,
						'cropStages': [
							{
								'fieldId': this.getFieldByPosition(this.currentEnrollment, 1).id,
								'stage': this.firstCropStage
							},
							{
								'fieldId': this.getFieldByPosition(this.currentEnrollment, 2).id,
								'stage': this.secondCropStage
							}
						]
					}, 'POST', '_blank');
				}
			}
		}
	}
</script>




// WEBPACK FOOTER //
// sharedinsights-download-form.vue?31d4a170